/**
 * Split blocks, in their order, over columns so that the highest column is as low as possible:
 * each column holds a run of blocks above each other, read from top to bottom and then the next
 * column; a block two columns wide takes two columns of its own
 * @param {Array<{ height: number, width: number }>} blocks height and width in columns of each block, in order
 * @param {number} columns number of columns there is room for
 * @returns {Array<{ column: number, row: number }>} first column and row of each block, counted from
 *   0; empty when the wide blocks can't be placed, such as a wide block after others in two columns
 */
export const splitIntoColumns = ( blocks, columns ) =>
{
  const count = blocks.length
  if ( !count ) return []

  // highest column for the first i blocks in c columns, and where the last run of those starts
  const high = Array.from( { length: count + 1 }, () => new Array( columns + 1 ).fill( Infinity ) )
  const start = Array.from( { length: count + 1 }, () => new Array( columns + 1 ).fill( -1 ) )
  high[ 0 ].fill( 0 )

  for ( let i = 1; i <= count; i++ )
  {
    for ( let c = 1; c <= columns; c++ )
    {
      // the last column holds blocks j up to i, as long as they are one column wide
      let height = 0
      for ( let j = i - 1; j >= 0 && blocks[ j ].width === 1; j-- )
      {
        height += blocks[ j ].height
        const highest = Math.max( high[ j ][ c - 1 ], height )
        if ( highest < high[ i ][ c ] )
        {
          high[ i ][ c ] = highest
          start[ i ][ c ] = j
        }
      }

      // or the last block is wide and takes the last two columns
      const last = blocks[ i - 1 ]
      if ( last.width > 1 && c >= last.width )
      {
        const highest = Math.max( high[ i - 1 ][ c - last.width ], last.height )
        if ( highest < high[ i ][ c ] )
        {
          high[ i ][ c ] = highest
          start[ i ][ c ] = i - 1
        }
      }
    }
  }

  // the fewest columns that give the lowest result, so empty columns stay on the right
  let used = 1
  for ( let c = 2; c <= columns; c++ )
  {
    if ( high[ count ][ c ] < high[ count ][ used ] ) used = c
  }
  if ( high[ count ][ used ] === Infinity ) return []

  // the runs from the last one back, then placed from the left
  const runs = []
  for ( let i = count, c = used; i > 0; )
  {
    const j = start[ i ][ c ]
    const width = blocks[ j ].width > 1 && j === i - 1 ? blocks[ j ].width : 1
    runs.unshift( { from: j, to: i, width } )
    i = j
    c -= width
  }

  const places = []
  let column = 0
  runs.forEach( ( { from, to, width } ) =>
  {
    let row = 0
    for ( let b = from; b < to; b++ )
    {
      places[ b ] = { column, row }
      row += blocks[ b ].height
    }
    column += width
  } )

  return places
}
